import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { MapPin, Plane, Train, Hotel, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useConference } from '@/context/ConferenceContext';

const VenueTravel = () => {
  const { importantDetails, siteConfig, getConferenceVenue } = useConference();
  const [locationInfo, setLocationInfo] = useState('');

  useEffect(() => {
    const fetchLocationInfo = async () => {
      const venue = getConferenceVenue();
      if (!venue) return;

      try {
        const response = await fetch(`https://en.wikipedia.org/api/rest_v1/page/summary/${encodeURIComponent(venue)}`);
        if (response.ok) {
          const data = await response.json();
          setLocationInfo(data.extract || '');
        } else {
          // Fallback to Barcelona
          setLocationInfo('Barcelona is a vibrant city located on the northeastern coast of Spain, known for its unique architecture, rich cultural heritage, and Mediterranean climate. As the capital of Catalonia, it offers a perfect blend of historic charm and modern innovation, making it an ideal venue for international conferences.');
        }
      } catch (error) {
        console.error('Failed to fetch location info:', error);
        setLocationInfo('Barcelona is a vibrant city located on the northeastern coast of Spain, known for its unique architecture, rich cultural heritage, and Mediterranean climate. As the capital of Catalonia, it offers a perfect blend of historic charm and modern innovation, making it an ideal venue for international conferences.');
      }
    };

    fetchLocationInfo();
  }, [getConferenceVenue]);
  return (
    <>
      <Helmet>
        <title>Venue & Travel - {importantDetails?.ConferenceTitle?.replace(/<[^>]*>/g, '') || siteConfig.name}</title>
        <meta name="description" content="Venue information and travel guide for ICCE 2026" />
      </Helmet>

      <div className="bg-white min-h-screen">
        {/* Hero */}
        <section className="hero-gradient py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Venue & Travel</h1>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                All you need to know about getting to and staying in {getConferenceVenue()}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Venue Details */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Conference Venue</h2>
                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm mb-6">
                  <div className="flex items-start gap-3 mb-4">
                    <MapPin className="text-sky-500 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{siteConfig?.venue?.name || 'Palais des Congrès de Paris'}</h3>
                      <p className="text-gray-600">{siteConfig?.venue?.address || '2 Place de la Porte Maillot'}</p>
                      <p className="text-gray-600">
                        {getConferenceVenue()}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">About the Venue</h3>
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {locationInfo || 'The Palais des Congrès de Paris is a premier convention center located in the heart of Paris, near the iconic Porte Maillot. This modern facility offers state-of-the-art conference spaces, exhibition halls, and advanced technology infrastructure for international conferences and events.'}
                  </p>
                  <p className="text-gray-600 leading-relaxed">
                    The venue features multiple auditoriums, meeting rooms, exhibition spaces, high-speed Wi-Fi throughout, and on-site catering services. Its strategic location provides excellent access to Paris's extensive public transportation network and major attractions.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Location Map</h2>
                <div className="bg-white rounded-2xl border-2 border-gray-100 shadow-sm overflow-hidden h-96">
                  <iframe
                    src={siteConfig?.venue?.mapUrl || 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2624.142047770137!2d2.284550315674!3d48.878931979218!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47e66fec70fb1d8f%3A0xd9dc8be3b26c7c5!2sPalais%20des%20Congr%C3%A8s%20de%20Paris!5e0!3m2!1sen!2sfr!4v1234567890123!5m2!1sen!2sfr'}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Venue Location"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Getting There */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Getting There</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Plane className="text-sky-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">By Air</h3>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Paris Charles de Gaulle Airport (CDG)</span>
                      <br />
                      Distance: 25 km
                      <br />
                      Travel time: 45-60 minutes by car/taxi
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Paris Orly Airport (ORY)</span>
                      <br />
                      Distance: 18 km
                      <br />
                      Travel time: 30-45 minutes by car/taxi
                    </p>
                    <p className="text-gray-600 text-sm mt-3">
                      RER B train provides direct service from CDG to central Paris. Roissybus connects CDG to Place de l'Opéra. Taxis, rideshare services, and rental cars are readily available at both airports.
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-white p-6 rounded-2xl shadow-sm"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Train className="text-sky-500" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">By Train/Public Transit</h3>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Métro Line 1</span>
                      <br />
                      Nearest station: Porte Maillot (direct access)
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">RER Line C</span>
                      <br />
                      Station: Porte Maillot (regional trains)
                    </p>
                    <p className="text-gray-600 mb-2">
                      <span className="font-medium">Bus Lines</span>
                      <br />
                      Multiple bus routes serve Porte Maillot
                    </p>
                    <p className="text-gray-600 text-sm mt-3">
                      Paris has an extensive public transportation network. Conference attendees receive a complimentary Paris Visite pass for unlimited travel on Métro, RER, and buses.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="mt-6 bg-sky-50 p-6 rounded-2xl">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Parking Information</h3>
              <p className="text-gray-700 mb-2">
                Parking is available at the venue (€15-20/day). We recommend using Paris's excellent public transportation system. Several nearby parking facilities offer daily rates of €20-30. The venue is also easily accessible by taxi or rideshare services.
              </p>
            </div>
          </div>
        </section>

        {/* Accommodation */}
        <section className="section-spacing">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Recommended Hotels</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {(siteConfig?.hotels || [
                {
                  name: 'Shangri-La Paris',
                  distance: '2.1 km',
                  rate: '€450/night',
                  phone: '+33 1 53 67 19 98',
                },
                {
                  name: 'Hotel Plaza Athénée',
                  distance: '3.2 km',
                  rate: '€650/night',
                  phone: '+33 1 53 67 66 65',
                },
                {
                  name: 'Le Royal Monceau Raffles Paris',
                  distance: '1.8 km',
                  rate: '€550/night',
                  phone: '+33 1 42 99 88 00',
                },
              ]).map((hotel, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card-hover bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm"
                  data-testid={`hotel-${index}`}
                >
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Hotel className="text-sky-500" size={20} />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{hotel.name}</h3>
                    </div>
                  </div>
                  <div className="space-y-2 text-sm">
                    <p className="text-gray-600">
                      <span className="font-medium">Distance:</span> {hotel.distance}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Rate:</span> {hotel.rate}
                    </p>
                    <p className="text-gray-600">
                      <span className="font-medium">Phone:</span> {hotel.phone}
                    </p>
                  </div>
                  <p className="text-xs text-sky-600 mt-4 font-medium">
                    Quote conference code "CCAI2026" for special rates
                  </p>
                </motion.div>
              ))}
            </div>

            <div className="mt-8 bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Accommodation Tips</h3>
              <ul className="text-gray-600 space-y-2">
                <li>• Book early to secure the best rates and availability</li>
                <li>• Mention the conference code "CCAI2026" when booking to receive negotiated rates</li>
                <li>• Most hotels offer free cancellation up to 48 hours before arrival</li>
                <li>• Consider hotels near Métro Line 1 for easy access to the venue</li>
                <li>• Contact the conference secretariat if you need assistance with accommodation</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Local Guide */}
        <section className="section-spacing bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Explore Paris</h2>
            <p className="text-gray-600 mb-8">
              Take advantage of your visit to explore the City of Light. Download our local guide for recommendations on dining, attractions, and activities in Paris.
            </p>
            <Button className="bg-sky-500 hover:bg-sky-600" data-testid="download-guide-btn">
              <Download size={18} className="mr-2" />
              Download Local Guide (PDF)
            </Button>

            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 text-left">
              <div className="bg-white p-4 rounded-xl">
                <p className="font-semibold text-gray-900 mb-1">Eiffel Tower</p>
                <p className="text-sm text-gray-600">Iconic landmark</p>
              </div>
              <div className="bg-white p-4 rounded-xl">
                <p className="font-semibold text-gray-900 mb-1">Louvre Museum</p>
                <p className="text-sm text-gray-600">World-class art</p>
              </div>
              <div className="bg-white p-4 rounded-xl">
                <p className="font-semibold text-gray-900 mb-1">Notre-Dame</p>
                <p className="text-sm text-gray-600">Historic cathedral</p>
              </div>
              <div className="bg-white p-4 rounded-xl">
                <p className="font-semibold text-gray-900 mb-1">Champs-Élysées</p>
                <p className="text-sm text-gray-600">Famous avenue</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default VenueTravel;
